import { useMemo } from 'react'

import type {
  EChartsOption,
  GridComponentOption,
  LineSeriesOption,
  XAXisComponentOption,
  YAXisComponentOption,
} from 'echarts'

import { type PeriodWindow, buildMidnightMarkLineSeries, makeTimeAxis } from '@/components/charts/history/timeAxis'
import { STATUS_AXIS_PX, STATUS_GRID_LEFT } from '@/insights/assets/historyGraph/constants'
import { buildGeofenceMarkAreas } from '@/insights/assets/historyGraph/transform'
import { convertCelsius, tempUnitSuffix } from '@/utils/conversions'
import { formatHistoryDateTime } from '@/utils/formatDate'

import { NumericSample, TrackerHistory } from '../types'

/** Temperature panel height (its own canvas, pinned above the metrics). */
export const TEMP_PANEL_PX = 140
/** Height of each metric panel (battery / humidity / light). */
export const METRIC_PANEL_PX = 140
/** Gap between stacked panels, and below the last one. */
const PANEL_GAP = 20
/** Top inset inside each canvas. */
const CANVAS_TOP = 16

export interface TrackerCanvas {
  option: EChartsOption
  height: number
}

const TEMP_COLOR = '#e11d48' // rose-600
const BATTERY_COLOR = '#16a34a' // green-600
const HUMIDITY_COLOR = '#0ea5e9' // sky-500
const LIGHT_COLOR = '#f59e0b' // amber-500

const SPLIT_LINE = { show: true, lineStyle: { color: '#d1d5db', opacity: 0.5 } } as const

const toPairs = (samples: NumericSample[]): [number, number][] =>
  samples.map((s) => [new Date(s.time).getTime(), s.value])

/**
 * ECharts options for the tracker history chart, split across two canvases: a
 * pinned temperature panel (geofence bands + midnight dividers + shipment range
 * marklines) and a scrollable stack of battery / humidity / light panels.
 *
 * Both canvases share the same time window and the same left/right grid insets,
 * so a timestamp lands on the identical x pixel in each — that is what lets the
 * pinned temperature axis read as the time axis for the panels scrolled below
 * it. Cross-canvas zoom and axis pointer are wired by `echarts.connect` in
 * TrackerHistoryChart; `axisPointer.link` only reaches within one instance.
 */
export function useTrackerChartOptions(
  data: TrackerHistory | undefined,
  periodWindow: PeriodWindow,
  zone: string,
  units: 'Imperial' | 'Metric',
  shipmentRange?: { min: number | null; max: number | null }
): { temp: TrackerCanvas; metrics: TrackerCanvas } {
  return useMemo(() => {
    const empty: TrackerCanvas = { option: {}, height: 0 }
    if (!data) return { temp: empty, metrics: empty }
    const degrees = tempUnitSuffix(units)

    const fmtTime = (v: string | number): string => formatHistoryDateTime(v, zone)

    // Nearest location sample within 15 minutes of a point, for tooltips —
    // mirrors the asset chart's findAddress, but trackers only have coordinates
    // (no reverse-geocoded address yet).
    const locationTimes = data.location.map((l) => ({
      t: new Date(l.time).getTime(),
      text: `${l.latitude.toFixed(4)}, ${l.longitude.toFixed(4)}`,
    }))
    const findLocation = (timeValue: number): string | undefined => {
      if (!locationTimes.length) return undefined
      let lo = 0
      let hi = locationTimes.length - 1
      while (lo < hi) {
        const mid = (lo + hi) >> 1
        if (locationTimes[mid].t < timeValue) lo = mid + 1
        else hi = mid
      }
      let best = lo
      if (lo > 0 && Math.abs(locationTimes[lo - 1].t - timeValue) < Math.abs(locationTimes[lo].t - timeValue)) {
        best = lo - 1
      }
      return Math.abs(locationTimes[best].t - timeValue) <= 15 * 60000 ? locationTimes[best].text : undefined
    }
    const tooltipLines = (label: string, valueText: string, time: number): string => {
      const lines = [`<b>${label}</b>: ${valueText}`, fmtTime(time)]
      const loc = findLocation(time)
      if (loc) lines.push(loc)
      return lines.join('<br/>')
    }

    const hasBattery = data.battery.length > 0
    const hasHumidity = data.humidity.length > 0
    const hasLight = data.light.length > 0

    // Grids, axes and series are coupled by array position — a series points at
    // its grid by numeric index. Each canvas therefore owns its own arrays and
    // its own index sequence, both starting at zero; slicing one shared set
    // would silently renumber every reference.
    const makeCanvas = () => {
      const grids: GridComponentOption[] = []
      const xAxes: XAXisComponentOption[] = []
      const yAxes: YAXisComponentOption[] = []
      const series: LineSeriesOption[] = []

      const addGrid = (gridDef: Record<string, unknown>, showAxisLabel: boolean): number => {
        const gridIndex = grids.length
        // Darker border than the in-grid split lines so the stacked panels read
        // as separate charts.
        grids.push({
          left: STATUS_GRID_LEFT,
          right: 30,
          show: true,
          borderWidth: 1,
          borderColor: '#9ca3af',
          ...gridDef,
        })
        xAxes.push(makeTimeAxis({ gridIndex, showLabel: showAxisLabel, periodWindow, zone }))
        return gridIndex
      }

      const build = (height: number): TrackerCanvas => {
        const indexes = xAxes.map((_, i) => i)
        return {
          height,
          option: {
            animation: false,
            grid: grids,
            xAxis: xAxes,
            yAxis: yAxes,
            tooltip: { trigger: 'item', confine: true },
            axisPointer: { link: [{ xAxisIndex: indexes }] },
            dataZoom: [
              {
                type: 'inside',
                xAxisIndex: indexes,
                filterMode: 'none',
                // The canvases live in a scroll container, so a bare wheel must
                // scroll the panels rather than zoom. Shift is the only safe
                // modifier: macOS binds ctrl+scroll to system screen zoom, and
                // Firefox binds alt+scroll to history back/forward.
                zoomOnMouseWheel: 'shift',
                moveOnMouseWheel: false,
                minSpan: 0,
                maxSpan: 100,
                throttle: 30,
              },
            ],
            series,
          } as EChartsOption,
        }
      }

      return { yAxes, series, addGrid, build }
    }

    const tempCanvas = makeCanvas()
    const metricsCanvas = makeCanvas()

    // --- Temperature canvas ---
    // Alone in its canvas, so it always draws the time axis: it stays pinned
    // while the metric panels scroll, and serves as their axis too.
    const tempGrid = tempCanvas.addGrid({ top: CANVAS_TOP, height: TEMP_PANEL_PX }, true)
    const tempPairs: [number, number][] = data.temperature.map((s) => [
      new Date(s.time).getTime(),
      convertCelsius(s.value, units),
    ])
    let yMin = Infinity
    let yMax = -Infinity
    for (const [, v] of tempPairs) {
      if (v < yMin) yMin = v
      if (v > yMax) yMax = v
    }
    const rangeMin = shipmentRange?.min != null ? Number(convertCelsius(shipmentRange.min, units).toFixed(1)) : null
    const rangeMax = shipmentRange?.max != null ? Number(convertCelsius(shipmentRange.max, units).toFixed(1)) : null
    if (rangeMin != null) yMin = Math.min(yMin, rangeMin)
    if (rangeMax != null) yMax = Math.max(yMax, rangeMax)
    if (yMin === Infinity) {
      yMin = -10
      yMax = 30
    }
    const pad = (yMax - yMin || 1) * 0.05
    tempCanvas.yAxes.push({
      type: 'value',
      gridIndex: tempGrid,
      min: Math.floor(yMin - pad),
      max: Math.ceil(yMax + pad),
      name: degrees,
      nameLocation: 'middle',
      nameGap: 40,
      splitLine: SPLIT_LINE,
    })

    // Shipment temperature range marklines (dashed), when the shipment defines one.
    const rangeMarkLines = []
    if (rangeMin != null) {
      rangeMarkLines.push({
        yAxis: rangeMin,
        lineStyle: { color: '#3b82f6', type: 'dashed' as const, width: 1 },
        label: { show: true, position: 'insideEndTop' as const, formatter: `Min ${rangeMin}${degrees}`, fontSize: 9 },
      })
    }
    if (rangeMax != null) {
      rangeMarkLines.push({
        yAxis: rangeMax,
        lineStyle: { color: '#ef4444', type: 'dashed' as const, width: 1 },
        label: { show: true, position: 'insideEndTop' as const, formatter: `Max ${rangeMax}${degrees}`, fontSize: 9 },
      })
    }

    // Visible point symbols are the hover targets for the item-trigger tooltip
    // (matching the asset chart) — without them no tooltip ever fires.
    tempCanvas.series.push({
      type: 'line',
      name: 'Temperature',
      xAxisIndex: tempGrid,
      yAxisIndex: tempGrid,
      data: tempPairs,
      showSymbol: true,
      symbol: 'circle',
      symbolSize: 5,
      sampling: 'lttb',
      lineStyle: { width: 2, color: TEMP_COLOR },
      itemStyle: { color: TEMP_COLOR },
      z: 3,
      tooltip: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter: (params: any) => {
          const [time, value] = params.data as [number, number]
          return tooltipLines('Temperature', `${value.toFixed(1)}${degrees}`, time)
        },
      },
      ...(rangeMarkLines.length ? { markLine: { silent: true, symbol: 'none', data: rangeMarkLines } } : {}),
    })

    // Geofence bands — location samples carry no geofence yet, so this renders
    // nothing until the API supplies one (transform returns empty markAreas).
    const hasGeofenceData = data.location.some((l) => (l as { geofence?: unknown }).geofence != null)
    if (hasGeofenceData) {
      const geofenceResult = buildGeofenceMarkAreas(
        data.location.map((l) => ({
          time: l.time,
          lat: l.latitude,
          lng: l.longitude,
          geofence: (l as { geofence?: { id: number; name: string } }).geofence,
        }))
      )
      if (geofenceResult.markArea.data?.length) {
        tempCanvas.series.push({
          type: 'line',
          name: '__geofence__',
          xAxisIndex: tempGrid,
          yAxisIndex: tempGrid,
          data: [],
          silent: false,
          symbol: 'none',
          lineStyle: { width: 0 },
          markArea: geofenceResult.markArea,
          z: 0,
        })
      }
    }

    const midnightSeries = buildMidnightMarkLineSeries(periodWindow, zone, tempGrid, tempGrid)
    if (midnightSeries) tempCanvas.series.push(midnightSeries)

    // --- Metrics canvas: full-height panels stacked top-down ---
    // Each panel reserves its own axis strip below the plot. The pinned
    // temperature axis is aligned to the same window, but it sits hundreds of
    // pixels above a scrolled-to panel — too far to read a value against.
    let top = CANVAS_TOP
    const nextTop = (): number => {
      const t = top
      top += METRIC_PANEL_PX + STATUS_AXIS_PX + PANEL_GAP
      return t
    }

    const addPanel = (
      name: string,
      color: string,
      samples: NumericSample[],
      format: (v: number) => string,
      // Percent metrics pin to 0–100 so the line's position reads as the level;
      // lux is unbounded so it auto-scales (floored at 0 = darkness). minAxisMax
      // keeps tiny values (a 0.1-lux night) near the BOTTOM of the panel instead
      // of auto-scaling a flat dark line up to the top.
      range: { min: number; max: number | 'dataMax'; minAxisMax?: number }
    ) => {
      const gridIndex = metricsCanvas.addGrid({ top: nextTop(), height: METRIC_PANEL_PX }, true)
      // A bounded range uses its declared min/max exactly, so the visible ticks
      // stay honest (0–100%, never -6 or 106). Only the unbounded lux axis takes
      // the 6% inset that keeps a pinned line off the grid border.
      const effectiveMax = (dataMax: number) => Math.max(dataMax, range.minAxisMax ?? 0)
      const bounded = range.max !== 'dataMax'
      const axisMin = bounded
        ? range.min
        : (v: { max: number }) => range.min - (effectiveMax(v.max) - range.min || 1) * 0.06
      const axisMax = bounded
        ? range.max
        : (v: { max: number }) => effectiveMax(v.max) + (effectiveMax(v.max) - range.min || 1) * 0.06
      metricsCanvas.yAxes.push({
        type: 'value',
        gridIndex,
        min: axisMin,
        max: axisMax,
        axisLabel: { show: true, formatter: format },
        axisTick: { show: true },
        axisLine: { show: true },
        splitLine: SPLIT_LINE,
        name,
        nameLocation: 'middle',
        nameRotate: 90,
        nameGap: 60,
        nameTextStyle: { fontSize: 11, color: '#374151' },
      })
      metricsCanvas.series.push({
        type: 'line',
        name,
        xAxisIndex: gridIndex,
        yAxisIndex: gridIndex,
        data: toPairs(samples),
        showSymbol: true,
        symbol: 'circle',
        symbolSize: 5,
        sampling: 'lttb',
        lineStyle: { width: 2, color },
        itemStyle: { color },
        z: 3,
        tooltip: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          formatter: (params: any) => {
            const [time, value] = params.data as [number, number]
            return tooltipLines(name, format(value), time)
          },
        },
      })
      const panelMidnights = buildMidnightMarkLineSeries(periodWindow, zone, gridIndex, gridIndex)
      if (panelMidnights) metricsCanvas.series.push(panelMidnights)
    }

    if (hasBattery) {
      addPanel('Battery', BATTERY_COLOR, data.battery, (v) => `${Math.round(v)}%`, { min: 0, max: 100 })
    }
    if (hasHumidity) {
      addPanel('Humidity', HUMIDITY_COLOR, data.humidity, (v) => `${Math.round(v)}%`, { min: 0, max: 100 })
    }
    if (hasLight) {
      addPanel(
        'Light',
        LIGHT_COLOR,
        data.light,
        // Sub-10 lux readings keep a decimal so dim-but-lit doesn't display as "0 lux".
        (v) => `${v < 10 ? v.toFixed(1) : Math.round(v).toLocaleString()} lux`,
        // ~100 lux ≈ dim indoor light: never scale the axis below that, so a
        // night of near-zero readings hugs the bottom instead of reading bright.
        { min: 0, max: 'dataMax', minAxisMax: 100 }
      )
    }

    return {
      temp: tempCanvas.build(CANVAS_TOP + TEMP_PANEL_PX + STATUS_AXIS_PX),
      metrics: metricsCanvas.build(top === CANVAS_TOP ? 0 : top),
    }
  }, [data, periodWindow, zone, units, shipmentRange])
}
